import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { getActiveClasses } from './classService';
import { getJourneysByClass } from './journeyService';
import { computeMasteryScore, retentionToFactor } from './tamkeenEngine';

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
const FOURTEEN_DAYS = 14 * 24 * 60 * 60 * 1000;
const AT_RISK_MASTERY = 40;
const BATCH_SIZE = 10;

let _subjectsCache: SubjectSummary[] | null = null;
let _subjectsCacheTs = 0;
const SUBJECTS_CACHE_TTL = 5 * 60 * 1000;

export interface SubjectSummary {
  id: string;
  name: string;
  enrolled: number;
  active: number;
  started: number;
  completed: number;
  journeyCount: number;
  totalCards: number;
}

export interface StudentEntry {
  uid: string;
  name: string;
  mastery: number | null;
  accuracy: number | null;
  studied: number;
  reason: string | null;
}

export interface JourneyEntry {
  id: string;
  title: string;
  started: number;
  completed: number;
  completionRate: number;
  dropoffStep: string | null;
  steps: { index: number; title: string; type: string; completed: number }[];
}

export interface SubjectDetail {
  className: string;
  enrolled: number;
  active: number;
  started: number;
  completed: number;
  journeyCount: number;
  totalCards: number;
  avgMastery: number | null;
  atRisk: number;
  unstudiedCount: number;
  journeys: JourneyEntry[];
  hardestCards: { id: string; c: number; w: number; total: number; errorRate: number | null }[];
  unstudiedCards: number;
  unstudiedCardIds: string[];
  students: StudentEntry[];
  lastRebuilt?: unknown;
}

export function invalidateSubjectsListCache(): void {
  _subjectsCache = null;
  _subjectsCacheTs = 0;
}

function collectCardIds(journeys: Record<string, unknown>[]): Set<string> {
  const ids = new Set<string>();
  for (const j of journeys) {
    if (j.trashedAt || j.active === false) continue;
    for (const g of ((j.groups || []) as Record<string, unknown>[])) {
      const cids = (g.cardIds || g.wordCardIds || g.flashcardIds || g.questionCardIds || []) as string[];
      cids.forEach((id) => ids.add(id));
    }
  }
  return ids;
}

async function runInBatches<T, R>(items: T[], batchSize: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(fn));
    results.push(...batchResults);
  }
  return results;
}

function toTimestampMs(ts: unknown): number {
  if (!ts) return 0;
  if (typeof (ts as { toDate?: () => Date }).toDate === 'function') return (ts as { toDate: () => Date }).toDate().getTime();
  if ((ts as { seconds?: number }).seconds) return (ts as { seconds: number }).seconds * 1000;
  const d = new Date(ts as string);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

export async function getSubjectsList({ skipCache = false } = {}): Promise<SubjectSummary[]> {
  if (!skipCache && _subjectsCache && (Date.now() - _subjectsCacheTs) < SUBJECTS_CACHE_TTL) {
    return _subjectsCache;
  }

  const classes = await getActiveClasses();

  const results = await Promise.all(classes.map(async (cls) => {
    try {
      const statsSnap = await getDoc(doc(db, 'classStats', cls.id));
      if (statsSnap.exists() && statsSnap.data().lastRebuilt) {
        const s = statsSnap.data();

        if (s.enrolled !== undefined) {
          return {
            id: cls.id,
            name: (s.className as string) || cls.name,
            enrolled: s.enrolled as number,
            active: (s.active as number) || 0,
            started: (s.started as number) || 0,
            completed: (s.completed as number) || 0,
            journeyCount: (s.journeyCount as number) || 0,
            totalCards: (s.totalCards as number) || 0,
          };
        }

        const students = Object.values((s.students || {}) as Record<string, Record<string, unknown>>);
        const now = Date.now();
        let activeCount = 0, startedCount = 0, completedCount = 0;
        for (const st of students) {
          const lastSeenMs = toTimestampMs(st.lastSeen);
          if (lastSeenMs > 0 && (now - lastSeenMs) < SEVEN_DAYS) activeCount++;
          if (st.startedAny) startedCount++;
          if (st.completedAll) completedCount++;
        }
        return {
          id: cls.id,
          name: (s.className as string) || cls.name,
          enrolled: students.length,
          active: activeCount,
          started: startedCount,
          completed: completedCount,
          journeyCount: (s.journeyCount as number) || 0,
          totalCards: (s.totalCards as number) || 0,
        };
      }
    } catch { /* classStats unavailable */ }

    return {
      id: cls.id,
      name: cls.name,
      enrolled: 0,
      active: 0,
      started: 0,
      completed: 0,
      journeyCount: 0,
      totalCards: 0,
    };
  }));

  _subjectsCache = results;
  _subjectsCacheTs = Date.now();
  return results;
}

function buildFromClassStats(stats: Record<string, unknown>): SubjectDetail {
  const studentsMap = (stats.students || {}) as Record<string, Record<string, unknown>>;
  const now = Date.now();
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

  const studentsList: StudentEntry[] = [];
  let activeCount = 0, startedCount = 0, completedCount = 0;
  let totalMastery = 0, masteryCount = 0;

  for (const [uid, s] of Object.entries(studentsMap)) {
    const lastSeenMs = toTimestampMs(s.lastSeen);
    const isActive = lastSeenMs > 0 && (now - lastSeenMs) < SEVEN_DAYS_MS;
    const isInactive = lastSeenMs > 0 && (now - lastSeenMs) > FOURTEEN_DAYS_MS;
    const lowMastery = s.mastery != null && (s.mastery as number) < AT_RISK_MASTERY;

    let reason: string | null = null;
    if (!s.startedAny) reason = 'لم يبدأ';
    else if (isInactive) reason = 'متوقف';
    else if (s.completedAll && lowMastery) reason = 'أكمل لكن إتقانه ضعيف';
    else if (lowMastery) reason = 'إتقان منخفض';

    studentsList.push({
      uid,
      name: s.name as string,
      mastery: (s.mastery as number) ?? null,
      accuracy: (s.accuracy as number) ?? null,
      studied: (s.studied as number) || 0,
      reason,
    });

    if (isActive) activeCount++;
    if (s.startedAny) startedCount++;
    if (s.completedAll) completedCount++;
    if (s.mastery != null) { totalMastery += s.mastery as number; masteryCount++; }
  }

  const avgMastery = masteryCount > 0 ? Math.round(totalMastery / masteryCount) : null;
  const journeyList = Object.entries((stats.journeys || {}) as Record<string, Record<string, unknown>>).map(([id, j]) => ({ id, ...j })) as unknown as JourneyEntry[];

  return {
    className: (stats.className as string) || '',
    enrolled: studentsList.length,
    active: activeCount,
    started: startedCount,
    completed: completedCount,
    journeyCount: (stats.journeyCount as number) || 0,
    totalCards: (stats.totalCards as number) || 0,
    avgMastery,
    atRisk: studentsList.filter((s) => s.reason).length,
    unstudiedCount: (stats.unstudiedCount as number) || 0,
    journeys: journeyList,
    hardestCards: (stats.hardestCards || []) as SubjectDetail['hardestCards'],
    unstudiedCards: (stats.unstudiedCount as number) || 0,
    unstudiedCardIds: (stats.unstudiedCardIds || []) as string[],
    students: studentsList,
    lastRebuilt: stats.lastRebuilt || null,
  };
}

export async function getSubjectDetail(classId: string): Promise<SubjectDetail | null> {
  const statsSnap = await getDoc(doc(db, 'classStats', classId));
  if (statsSnap.exists() && statsSnap.data().lastRebuilt) {
    return buildFromClassStats(statsSnap.data());
  }
  return getSubjectDetailLive(classId);
}

async function getSubjectDetailLive(classId: string): Promise<SubjectDetail | null> {
  const [cls, journeys] = await Promise.all([
    (async () => {
      const snap = await getDoc(doc(db, 'classes', classId));
      return snap.exists() ? { id: snap.id, ...snap.data() } as { id: string; name: string; desiredRetention?: number } : null;
    })(),
    getJourneysByClass(classId),
  ]);

  if (!cls) return null;

  const activeJourneys = (journeys as Record<string, unknown>[]).filter((j) => !j.trashedAt && j.active !== false);
  const allCardIds = collectCardIds(activeJourneys);

  const studentsQ = query(collection(db, 'users'), where('role', '==', 'student'), where('classes', 'array-contains', classId));
  const studentsSnap = await getDocs(studentsQ);
  const students = studentsSnap.docs.map((d) => ({ uid: d.id, ...d.data() } as { uid: string; displayName?: string; phone?: string; lastSeen?: unknown; [key: string]: unknown }));

  const now = Date.now();
  const cardErrorMap: Record<string, { c: number; w: number }> = {};
  const cardStudiedBy: Record<string, number> = {};
  const journeyStats: Record<string, { title: string; started: number; completed: number; stepDropoff: Record<number, number> }> = {};

  for (const j of activeJourneys) {
    const steps = (j.steps || []) as { id: string; title?: string; type?: string }[];
    journeyStats[j.id as string] = { title: (j.title as string) || (j.id as string), started: 0, completed: 0, stepDropoff: {} };
    for (let i = 0; i < steps.length; i++) {
      journeyStats[j.id as string].stepDropoff[i] = 0;
    }
  }

  const allStudentStats: StudentEntry[] = [];

  interface StudentResult {
    jMap: Record<string, Record<string, unknown>>;
    stepsById: Record<string, Record<string, boolean>>;
    cardData: { id: string; c: number; w: number }[];
    mastery: { score: number; studied: number; declining: number };
    riskReason: string | null;
    student: { uid: string; displayName?: string; phone?: string; lastSeen?: unknown };
    startedAny: boolean;
    completedAll: boolean;
  }

  const studentResults = await runInBatches<typeof students[number], StudentResult>(students, BATCH_SIZE, async (student) => {
    const startedJourneyIds: string[] = [];
    const jSnap = await getDocs(collection(db, 'progress', student.uid, 'journeys'));
    const jMap: Record<string, Record<string, unknown>> = {};
    jSnap.docs.forEach((d) => { jMap[d.id] = d.data(); });

    for (const j of activeJourneys) {
      if (jMap[j.id as string]) startedJourneyIds.push(j.id as string);
    }

    const [cSnap, ...stepsSnaps] = await Promise.all([
      getDocs(collection(db, 'progress', student.uid, 'cards')),
      ...startedJourneyIds.map((jid) =>
        getDocs(collection(db, 'progress', student.uid, 'journeys', jid, 'steps'))
      ),
    ]);

    const stepsById: Record<string, Record<string, boolean>> = {};
    startedJourneyIds.forEach((jid, idx) => {
      const stepsDone: Record<string, boolean> = {};
      stepsSnaps[idx].docs.forEach((d) => { if (d.data().done) stepsDone[d.id] = true; });
      stepsById[jid] = stepsDone;
    });

    const statesMap: Record<string, unknown> = {};
    const cardData: { id: string; c: number; w: number }[] = [];
    cSnap.docs.forEach((d) => {
      if (allCardIds.has(d.id)) {
        const data = d.data();
        statesMap[d.id] = data;
        cardData.push({ id: d.id, c: (data.c as number) || 0, w: (data.w as number) || 0 });
      }
    });

    const m = computeMasteryScore(statesMap, retentionToFactor((cls as Record<string, unknown>).desiredRetention));
    const lastSeen = (student.lastSeen as { toDate?: () => Date })?.toDate?.() || student.lastSeen;
    const startedAny = startedJourneyIds.some((jid) => ((jMap[jid]?.doneCount as number) || 0) > 0);
    const completedAll = activeJourneys.length > 0 && activeJourneys.every((j) => jMap[j.id as string]?.done);
    const inactive = lastSeen && (now - new Date(lastSeen as string).getTime()) > FOURTEEN_DAYS;
    const lowMastery = m.score >= 0 && m.score < AT_RISK_MASTERY;

    let riskReason: string | null = null;
    if (!startedAny) riskReason = 'لم يبدأ';
    else if (inactive) riskReason = 'متوقف';
    else if (completedAll && lowMastery) riskReason = 'أكمل لكن إتقانه ضعيف';
    else if (lowMastery) riskReason = 'إتقان منخفض';

    return { jMap, stepsById, cardData, mastery: m, riskReason, student, startedAny, completedAll };
  });

  let activeCount = 0;
  let startedCount = 0;
  let completedCount = 0;
  let totalMastery = 0;
  let masteryCount = 0;

  for (const r of studentResults) {
    const lastSeen = (r.student.lastSeen as { toDate?: () => Date })?.toDate?.() || r.student.lastSeen;
    if (lastSeen && (now - new Date(lastSeen as string).getTime()) < SEVEN_DAYS) activeCount++;
    if (r.startedAny) startedCount++;
    if (r.completedAll) completedCount++;
    if (r.mastery.score >= 0) { totalMastery += r.mastery.score; masteryCount++; }

    for (const j of activeJourneys) {
      const jp = r.jMap[j.id as string];
      if (jp) {
        const stepsDone = r.stepsById[j.id as string] || {};
        const steps = (j.steps || []) as { id: string; title?: string; type?: string }[];
        const doneAnyStep = steps.some((s) => stepsDone[s.id]);

        if (doneAnyStep) journeyStats[j.id as string].started++;
        if (jp.done) journeyStats[j.id as string].completed++;

        for (let i = 0; i < steps.length; i++) {
          if (stepsDone[steps[i].id]) {
            journeyStats[j.id as string].stepDropoff[i] = (journeyStats[j.id as string].stepDropoff[i] || 0) + 1;
          }
        }
      }
    }

    for (const cd of r.cardData) {
      if (!cardStudiedBy[cd.id]) cardStudiedBy[cd.id] = 0;
      cardStudiedBy[cd.id]++;
      if (!cardErrorMap[cd.id]) cardErrorMap[cd.id] = { c: 0, w: 0 };
      cardErrorMap[cd.id].c += cd.c;
      cardErrorMap[cd.id].w += cd.w;
    }

    const totalC = r.cardData.reduce((s, cd) => s + cd.c, 0);
    const totalW = r.cardData.reduce((s, cd) => s + cd.w, 0);
    const accuracy = (totalC + totalW) > 0 ? Math.round((totalC / (totalC + totalW)) * 100) : null;

    allStudentStats.push({
      uid: r.student.uid,
      name: r.student.displayName || r.student.phone || 'بدون اسم',
      mastery: r.mastery.score >= 0 ? r.mastery.score : null,
      accuracy,
      studied: r.mastery.studied || 0,
      reason: r.riskReason,
    });
  }

  const hardestCards = Object.entries(cardErrorMap)
    .map(([id, { c, w }]) => ({ id, c, w, total: c + w, errorRate: (c + w) >= 3 ? Math.round((w / (c + w)) * 100) : null }))
    .filter((x) => x.errorRate !== null && x.total >= 10)
    .sort((a, b) => (b.errorRate ?? 0) - (a.errorRate ?? 0))
    .slice(0, 15);

  const unstudiedCards = [...allCardIds].filter((id) => !cardStudiedBy[id]);

  const journeyList: JourneyEntry[] = activeJourneys.map((j) => {
    const s = journeyStats[j.id as string];
    const steps = (j.steps || []) as { id: string; title?: string; type?: string }[];
    let dropoffIndex: number | null = null;
    let maxDrop = 0;
    const stepCounts = [];
    for (let i = 0; i < steps.length; i++) {
      const count = s.stepDropoff[i] || 0;
      stepCounts.push({ index: i, title: steps[i].title || `محطة ${i + 1}`, type: steps[i].type || '', completed: count });
      if (i > 0) {
        const prev = s.stepDropoff[i - 1] || 0;
        const drop = prev - count;
        if (drop > maxDrop && prev > 0 && (drop / prev) > 0.3) {
          maxDrop = drop;
          dropoffIndex = i;
        }
      }
    }
    return {
      id: j.id as string,
      title: (j.title as string) || (j.id as string),
      started: s.started,
      completed: s.completed,
      completionRate: s.started > 0 ? Math.round((s.completed / s.started) * 100) : 0,
      dropoffStep: dropoffIndex !== null && s.started >= 20 ? (steps[dropoffIndex]?.title || `محطة ${dropoffIndex + 1}`) : null,
      steps: stepCounts,
    };
  });

  return {
    className: cls.name,
    enrolled: students.length,
    active: activeCount,
    started: startedCount,
    completed: completedCount,
    journeyCount: activeJourneys.length,
    totalCards: allCardIds.size,
    avgMastery: masteryCount > 0 ? Math.round(totalMastery / masteryCount) : null,
    atRisk: allStudentStats.filter((s) => s.reason).length,
    unstudiedCount: unstudiedCards.length,
    journeys: journeyList,
    hardestCards,
    unstudiedCards: unstudiedCards.length,
    unstudiedCardIds: unstudiedCards.slice(0, 20),
    students: allStudentStats,
  };
}
